import { IStringModule } from "../core/IStringModule";
import { EventManager } from "../core/managers/EventManager";
import { StringMirrorObject } from "./StringMirrorObject";

/**
 * Internal class representing a DOM-bound interactive object.
 * Connected to modules and holds its own internal state.
 */
export class StringObject {
  /**
   * The DOM element this object wraps.
   */
  public htmlElement: HTMLElement;

  /**
   * Unique global ID assigned by the system.
   */
  public id: string = "";

  /**
   * Space-separated list of all attribute keys associated with this object.
   */
  public keys: string[] = [];

  /**
   * Mirror objects linked via `string-copy-from`.
   */
  private mirrors = new Map<string, StringMirrorObject>();

  /**
   * Internal key-value store of dynamic object properties (like offsets, progress, etc.).
   */
  private properties: Map<string, any> = new Map();

  /**
   * Modules currently connected to this object.
   */
  private modules: IStringModule[] = [];

  /**
   * Manages and handles events for the object.
   * Provides functionality to register, trigger, and manage event listeners.
   */
  events: EventManager = new EventManager();

  constructor(id: string, element: HTMLElement) {
    this.htmlElement = element;
    this.id = id;
  }

  /**
   * Stores a property value for this object.
   * @param key - Property name
   * @param value - Value to store
   */
  public setProperty<T>(key: string, value: T): void {
    this.properties.set(key, value);
  }

  /**
   * Retrieves a previously stored property value.
   * @param key - Property name
   * @returns The value or null if not set
   */
  public getProperty<T>(key: string): T {
    return this.properties.get(key) ?? null;
  }

  /**
   * Marks this object as "active" (usually on intersection/scroll enter).
   */
  public enter(): void {
    this.events.emit("enter", this);
    this.setProperty("active", true);
    this.modules.forEach((module) => {
      module.enterObject(this.id, this);
    });
  }

  /**
   * Marks this object as "inactive" (usually on intersection/scroll leave).
   */
  public leave(): void {
    this.events.emit("leave", this);
    this.setProperty("active", false);
    this.modules.forEach((module) => {
      module.exitObject(this.id);
    });
  }

  /**
   * Removes the current object by iterating through all associated modules
   * and invoking their `removeObject` method with the object's ID.
   *
   * This method ensures that the object is properly removed from all
   * modules it is associated with.
   */
  public remove(): void {
    this.modules.forEach((module) => {
      module.removeObject(this.id);
    });
  }

  /**
   * Shows the object, applies visual class and notifies connected modules.
   */
  public show(): void {
    this.htmlElement.classList.add("-inview");
  }

  /**
   * Hides the object, removes visual class (if repeat is enabled), and notifies modules.
   */
  public hide(): void {
    const shouldRepeat = this.getProperty<boolean>("repeat");
    if (shouldRepeat) {
      this.htmlElement.classList.remove("-inview");
    }
  }

  /**
   * Connects a module to this object if not already connected.
   * @param module - The module to connect
   */
  public connect(module: IStringModule): void {
    if (!this.modules.includes(module)) {
      this.modules.push(module);
    }
  }

  public addMirror(mirror: StringMirrorObject): void {
    this.mirrors.set(mirror.id, mirror);
  }

  public removeMirror(id: string): void {
    this.mirrors.delete(id);
  }

  public get mirrorObjects(): StringMirrorObject[] {
    return Array.from(this.mirrors.values());
  }

  public get connects(): HTMLElement[] {
    return this.mirrorObjects.map((mirror) => mirror.htmlElement);
  }
}
